import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Sparkles } from 'lucide-react';

import {
  setSopApplicability,
  suggestSopApplicability,
  describeSopError,
  AiDisabledError,
  type ApplicabilitySuggestion,
  type ApplicabilityView,
  type SopApplicabilityInput,
} from '@/api/sops.api';
import { eosErrorCode } from '@/lib/eos/errors';
import { listDepartments } from '@/api/employees.api';
import { listSeats } from '@/api/seats.api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApplicabilityEditor } from '@/components/compliance/ApplicabilityEditor';
import { useToast } from '@/hooks/useToast';

interface Props {
  open: boolean;
  onClose: () => void;
  slug: string;
  initialApplicability: ApplicabilityView[];
}

function viewToInput(v: ApplicabilityView): SopApplicabilityInput {
  return {
    targetType: v.targetType,
    targetRole: v.targetRole,
    targetSeatId: v.targetSeatId,
    targetDepartmentId: v.targetDepartmentId,
    aiSuggested: v.aiSuggested,
  };
}

/**
 * Edit a SOP's applicability (ADR-037). Authors pick the roles / seats /
 * departments the procedure applies to — optionally seeding from an AI
 * suggestion — and record a `reasonForChange` (cGMP §111.180(c)). Mirrors the
 * SopTagsModal pattern; on success invalidates the SOP list + detail queries.
 */
export function ApplicabilityModal({ open, onClose, slug, initialApplicability }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [value, setValue] = useState<SopApplicabilityInput[]>(() => initialApplicability.map(viewToInput));
  const [suggestions, setSuggestions] = useState<ApplicabilitySuggestion[]>([]);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const seatsQuery = useQuery({
    queryKey: ['compliance-sop', 'applicability', 'seats'],
    queryFn: () => listSeats({ scenario: 'current' }),
    enabled: open,
  });
  const departmentsQuery = useQuery({
    queryKey: ['compliance-sop', 'applicability', 'departments'],
    queryFn: listDepartments,
    enabled: open,
  });

  const suggestMutation = useMutation({
    mutationFn: () => suggestSopApplicability(slug),
    onSuccess: (result) => {
      setSuggestions(result);
      setAiNotice(
        result.length === 0
          ? 'The assistant didn’t find any obvious roles, seats, or departments for this SOP.'
          : null,
      );
    },
    onError: (err) => {
      if (err instanceof AiDisabledError) {
        setAiNotice('AI suggestions aren’t configured for this organization — add applicability manually.');
        return;
      }
      const code = eosErrorCode(err);
      if (code === 'ai_could_not_suggest') {
        setAiNotice('The assistant couldn’t suggest applicability for this SOP — add it manually.');
        return;
      }
      setAiNotice('The assistant is temporarily unavailable — add applicability manually.');
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => setSopApplicability(slug, { applicability: value, reasonForChange: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-sop', slug] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-sops'] });
      toast.show('Applicability updated', { variant: 'success' });
      onClose();
    },
  });

  const canSubmit = reason.trim().length > 0 && !saveMutation.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    saveMutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit applicability" size="lg">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-kw-muted">
            Choose the roles, seats, or departments this SOP applies to.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              suggestMutation.mutate();
            }}
            isLoading={suggestMutation.isPending}
          >
            <Sparkles className="size-4" />
            Suggest with AI
          </Button>
        </div>

        {aiNotice ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3 text-[12px] text-kw-muted">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-kw-muted" />
            <span>{aiNotice}</span>
          </div>
        ) : null}

        <ApplicabilityEditor
          value={value}
          onChange={setValue}
          suggestions={suggestions}
          seats={seatsQuery.data ?? []}
          departments={departmentsQuery.data ?? []}
        />

        <Input
          label="Reason for change"
          name="applicability-reason"
          required
          maxLength={500}
          placeholder="e.g. mapped this SOP to the receiving seats after the reorg"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          hint="Required by cGMP §111.180(c). Captured in the audit log."
        />

        {saveMutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeSopError(saveMutation.error, 'Could not update the applicability.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending} disabled={!canSubmit}>
            Save applicability
          </Button>
        </div>
      </form>
    </Modal>
  );
}
